// ── Vendor Registration Data ──────────────────────────────────────────────
// Sourced directly from the "Sending Partner/Vendor Registration Forms"
// Confluence page (exported by the user). Templates keep the same bracketed
// placeholders the original process uses for anything we can't know
// automatically (ABN, estimated volume, industry) — fill those in before
// sending. Attachments listed here still need to be manually prepared
// (signed forms/screenshots) and attached — this tool can't fabricate them.

export interface VendorExtraField {
  key: string
  label: string
  placeholder: string
}

export interface VendorRegistration {
  id: string
  name: string
  country: string
  to: string[]
  cc?: string[]
  subjectTemplate: string
  bodyTemplate: string
  attachmentsNeeded: string[]
  sla: string
  note?: string
  extraFields: VendorExtraField[]
}

export const VENDOR_REGISTRATIONS: VendorRegistration[] = [
  {
    id: 'dvs-direct',
    name: 'DVS (Direct)',
    country: 'AU',
    to: ['IVS.Manager@ag.gov.au'],
    subjectTemplate: 'FrankieOne DVS Due Diligence - {companyName}',
    bodyTemplate: `Dear DVS Manager,

As an authorised DVS Gateway Service Provider, FrankieOne (Incorporated as Frankie Financial, ABN 61 623 506 892) confirms that it has conducted the relevant due diligence checks on {companyName} ({abn}) and has a record of its agreement to the DVS Business User Terms and Conditions of Use.

FrankieOne approves {companyName} ({abn}) access to the DVS as a Business User who has completed the online DVS New Business User form.

Anticipated annual use of the DVS by document type has not yet been advised to us and has been requested.

[Only for reporting entity] Please see attached the AEC declaration form related to the above company.

Kind regards,`,
    attachmentsNeeded: ['AEC Declaration Form (only if the company is a reporting entity)'],
    sla: '1 week (can extend if the customer is slow signing the Participation Agreement)',
    note: 'Send after the customer completes the DVS online form and "IVS Manager Noggin" notifies onboarding@frankieone.com.',
    extraFields: [{ key: 'abn', label: 'ABN', placeholder: 'ABN number' }],
  },
  {
    id: 'equifax-au',
    name: 'Equifax',
    country: 'AU',
    to: ['jennifer.voukidis@equifax.com'],
    cc: ['Troy.Bush@equifax.com', 'customersuccess.au@equifax.com'],
    subjectTemplate: 'New Branch Request - {companyName}',
    bodyTemplate: `Hi Jen,

Requesting the creation of a new branch for {companyName}, with an estimated annual volume of approximately {estVolume}.

Details:
IDM Onboarding Checklist: Attached
ABN: {abn}
Primary Industry: {industry}

Please let me know if you need any additional information to proceed.

Thanks,`,
    attachmentsNeeded: ['IDM Onboarding Checklist (with the correct Equifax profile selected)'],
    sla: '2 weeks',
    extraFields: [
      { key: 'abn', label: 'ABN', placeholder: 'ABN number' },
      { key: 'industry', label: 'Primary industry', placeholder: 'e.g. Fintech' },
      { key: 'estVolume', label: 'Estimated annual volume', placeholder: 'e.g. 1,000' },
    ],
  },
  {
    id: 'experian-au',
    name: 'Experian',
    country: 'AU',
    to: ['Dayane.Lourenco@experian.com'],
    subjectTemplate: 'Experian Credentials application form - {companyName}',
    bodyTemplate: `Hi Dayane,

I am looking to get Experian credentials set up for {companyName}.

I have attached:
- Credentialing application form
- Statement of work

{companyName}'s privacy policy can be found here (link website privacy policy), and their complaints policy can be found here (link website complaints/disputes policy).

Anything further required please let me know.

Kind regards,`,
    attachmentsNeeded: ['Application form (signed)', 'SOW extract — pages 1–2 + signature pages only, pricing masked'],
    sla: '1 week',
    note: 'Customer needs a publicly available privacy policy and complaints/disputes policy on their website before Experian will grant credentials.',
    extraFields: [],
  },
  {
    id: 'centrix-nz',
    name: 'Centrix',
    country: 'NZ',
    to: ['natasha.barker@centrix.co.nz'],
    subjectTemplate: 'FrankieOne - Centrix credentials for {companyName}',
    bodyTemplate: `Hi Natasha,

I am looking to get Centrix set up for {companyName}. Please find the attached forms. Their privacy policy can be found here.

Any issues let me know.

Kind regards,`,
    attachmentsNeeded: ['Centrix Application form', 'Centrix DIA application form (if applicable)'],
    sla: '2 weeks',
    note: 'Application form must be signed by a FrankieOne-authorised signee before sending.',
    extraFields: [],
  },
  {
    id: 'equifax-nz',
    name: 'Equifax',
    country: 'NZ',
    to: ['shinaed.mcgowan@equifax.com', 'emily.upton@equifax.com'],
    subjectTemplate: 'New Profile - {companyName}',
    bodyTemplate: `Hi Shinaed and Emily,

Estimated annual volumes: ~{estVolume}k per year.

Please find attached to set up credentials for {companyName}:
- Equifax Application Form
- XML Connect Form
- DIA Application Form
- Customer Terms & Conditions
- Customer Privacy Policy

Any issues please let me know.

Kind regards,`,
    attachmentsNeeded: ['Equifax New Subscription Form (signed by FrankieOne)', 'XML Connect Form', 'DIA Application Form', 'Customer Terms & Conditions', 'Customer Privacy Policy'],
    sla: '2 weeks',
    note: 'DIA form is only required for Passport Verification.',
    extraFields: [{ key: 'estVolume', label: 'Estimated annual volume (k)', placeholder: 'e.g. 5' }],
  },
  {
    id: 'superchoice',
    name: 'SuperChoice',
    country: 'AU',
    to: ['Rob.Woods@superchoice.com.au'],
    subjectTemplate: 'FrankieOne - Proof of consent language - {companyName}',
    bodyTemplate: `Dear Rob,

Please see attached proof of consent language as screenshotted from {companyName}'s platform for SuperChoice's approval.

Please let me know if there are any issues.

Kind regards,`,
    attachmentsNeeded: ['Screenshot of consent language (only needed if not using FrankieOne Smart UI)'],
    sla: 'No SLA specified',
    note: 'No registration required at all if the customer uses FrankieOne Smart UI.',
    extraFields: [],
  },
]

export function fillTemplate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const v = values[key]
    return v && v.trim() ? v.trim() : match
  })
}
